import { useCallback, useMemo } from 'react';
import { Button, Card, Dropdown, Form } from 'react-bootstrap';
import { BsList } from 'react-icons/bs';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import dateFormat from 'dateformat';
import { useFormikContext } from 'formik';

import '@components/Numbers/MyNumbers/MyNumbersTable.css';

import {
  ButtonSplitGroup,
  CustomToggleButton,
  ExportToCSVButton,
} from '@components/Buttons';
import { updateQueryStringOnFieldChange } from '@components/Filters/Filters';
import { DefaultSpinnerCenter } from '@components/Loader/Loader';
import { CancellationBadge } from '@components/Numbers/Badges';
import {
  BootstrapGenericTable,
  useTableRowsSelection,
} from '@components/Table';
import { SelectionColumn } from '@components/Table/Columns';
import { isValidDate } from '@features';
import { useThrottle } from '@features/useThrottle';
import { cancellationCartApi } from '@reducers/order';

const renderProfileHeader = (formikContext, searchParams, setSearchParams) => {
  const { handleChange, handleBlur, values } = formikContext;
  const throttle = useThrottle((event) => {
    updateQueryStringOnFieldChange(event, { searchParams, setSearchParams });
  }, 500);

  const throttledChangeHandler = useCallback(
    (event) => {
      handleChange(event);
      throttle(event);
    },
    [throttle]
  );
  return (
    <Form.Group controlId="formGroupProfile">
      <Form.Label>End user</Form.Label>
      <Form.Control
        type="input"
        name="profileName"
        size="sm"
        value={values.profileName}
        onChange={throttledChangeHandler}
        onBlur={handleBlur}
        placeholder="Search"
      />
    </Form.Group>
  );
};

const renderActionCell = ({ row }) => {
  const navigate = useNavigate();
  const { login } = useSelector((state) => state.auth.user);
  const [add] = cancellationCartApi.useAddDidToCartMutation();
  const { country, did } = row.values;
  const { id } = row.original;
  const detailsURL = `/numbers/${did}/details`;
  return (
    <Dropdown drop="end">
      <Dropdown.Toggle as={CustomToggleButton} id="dropdown-custom-components">
        <BsList size={22} />
      </Dropdown.Toggle>

      <Dropdown.Menu variant="dark" className="normalFontSize">
        <Dropdown.Item as={Link} eventKey="1" to={detailsURL}>
          Details
        </Dropdown.Item>
        <Dropdown.Item
          as={Link}
          eventKey="2"
          to={detailsURL}
          state={{ tabKey: 'profile' }}
        >
          Change Profile
        </Dropdown.Item>
        <Dropdown.Item
          as={Link}
          eventKey="3"
          to={detailsURL}
          state={{ tabKey: 'routing', activeKey: 'trunk' }}
        >
          Change Trunk
        </Dropdown.Item>
        <Dropdown.Item
          as={Link}
          eventKey="4"
          to={detailsURL}
          state={{ tabKey: 'routing', activeKey: ['routing', 'shadowRouting'] }}
        >
          Routing Number
        </Dropdown.Item>
        <Dropdown.Divider />
        <Dropdown.Item
          as={Link}
          eventKey="5"
          to="/tickets/create"
          state={{ did, country }}
        >
          Raise Ticket
        </Dropdown.Item>
        <Dropdown.Item
          eventKey="6"
          onClick={() => {
            add({ id, login })
              .unwrap()
              .then(() => navigate('/cart/cancellation'))
              .catch(() => {});
          }}
        >
          Delete
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
};

const renderNumberCell = ({ row, value }) => (
  <>
    <Link to={`/numbers/${value}/details`}>{value}</Link>
    <CancellationBadge isBeingCancelled={row.original.is_being_cancelled} />
  </>
);

const renderEarliestCancelCell = ({ value }) => {
  if (!isValidDate(value)) {
    return value;
  }

  const date = new Date(value);
  return dateFormat(date, 'defaultDate');
};

export const MyNumbersTable = ({ useData, searchParams, setSearchParams }) => {
  const navigate = useNavigate();
  const { login } = useSelector((state) => state.auth.user);
  const [addDids, { isLoading }] =
    cancellationCartApi.useAddDidsToCartMutation();
  const profileName = searchParams.get('profileName');
  const country = searchParams.get('country');
  const area = country ? searchParams.get('area') : null;
  const did = searchParams.get('did');
  const filterData = useMemo(
    () => ({
      country,
      area,
      did,
      profile: profileName,
    }),
    [country, area, did, profileName]
  );

  const { selectedRows, onRowSelectionChange, onAllRowsSelectionChange } =
    useTableRowsSelection();
  const exportContent = () => (
    <ButtonSplitGroup>
      <ExportToCSVButton
        to="customer/dids/export"
        filterData={{ ...filterData, csv_export: true }}
      />
      <Button
        size="sm"
        variant="didxl"
        disabled={selectedRows.length === 0 || isLoading}
        onClick={() => {
          const dids = selectedRows.map(({ id }) => id);
          addDids({ dids, login })
            .unwrap()
            .then(() => navigate('/cart/cancellation'))
            .catch(() => {});
        }}
      >
        {isLoading ? <DefaultSpinnerCenter /> : null}
        Cancel
      </Button>
    </ButtonSplitGroup>
  );

  const columns = useMemo(
    () => [
      SelectionColumn({
        className: 'col-md-1',
        controlled: true,
        onRowSelectionChange,
        onAllRowsSelectionChange,
      }),
      {
        id: 'did',
        Header: 'Number',
        accessor: 'did',
        className: 'col-md-1',
        Cell: renderNumberCell,
      },
      {
        id: 'profileName',
        Header: () => {
          const formikContext = useFormikContext();
          return renderProfileHeader(
            formikContext,
            searchParams,
            setSearchParams
          );
        },
        accessor: 'profile.name',
        className: 'col-md-2',
      },
      {
        id: 'area',
        Header: 'City/Area Code',
        accessor: 'area.name',
        filter: (rows, id, filterValue) =>
          filterValue
            ? rows.filter((row) => String(row.original.area.id) === filterValue)
            : rows,
        Cell: ({ row, value }) => `${value} (${row.original.area.prefix})`,
        className: 'col-md-2',
      },
      {
        id: 'country',
        Header: 'Country',
        accessor: 'country.name',
        filter: (rows, id, filterValue) =>
          filterValue
            ? rows.filter(
                (row) => String(row.original.country.id) === filterValue
              )
            : rows,
      },
      {
        id: 'plan',
        Header: 'Plan',
        accessor: 'plan',
        className: 'col-md-1',
      },
      {
        Header: 'MRC',
        accessor: 'mrc',
        className: 'col-md-1',
      },
      {
        Header: 'Trunk',
        accessor: 'customer_trunk.name',
        className: 'col-md-1',
      },
      {
        Header: 'Earliest Cancel',
        accessor: 'earliest_cancel',
        Cell: renderEarliestCancelCell,
      },
      {
        Header: 'Action',
        Cell: renderActionCell,
        accessor: 'action',
        className: 'col-md-1 text-center',
      },
    ],
    [country, area, did]
  );
  return (
    <Card>
      <Card.Header className="bg-didxl text-white">My numbers</Card.Header>
      <Card.Body>
        <div id="existing-numbers">
          <BootstrapGenericTable
            columns={columns}
            useData={useData}
            useDataArgs={{ customer: login }}
            filterData={filterData}
            tableProps={() => ({
              responsive: true,
              bordered: true,
              className: 'normalFontSize',
            })}
            paginated
            exportContent={exportContent}
            getRowId={(row) => row?.did}
            selectedRows
          />
        </div>
      </Card.Body>
    </Card>
  );
};
